import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Button, Box, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, InputLabel, FormControl, Checkbox, FormControlLabel,
    IconButton, Tooltip, TableSortLabel, Typography, Stack
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, History as HistoryIcon } from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useUsers from "../../hooks/useUsers";
import useCompanies from "../../hooks/useCompanies";

/* ---------- Sort helpers ---------- */
function getCellValue(u, key) {
    switch (key) {
        case "name":
            return `${u.firstname || ""} ${u.lastname || ""}`.trim();
        case "username":
            return u.username || "";
        case "email":
            return u.email || "";
        case "role":
            return u.role || "";
        case "company":
            return u.company_name || "";
        case "last_login":
            return u.last_login_at || "";
        default:
            return "";
    }
}

function formatDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Berlin",
    });
}

function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    const sa = (va ?? "").toString().toLowerCase();
    const sb = (vb ?? "").toString().toLowerCase();

    if (sb < sa) return -1;
    if (sb > sa) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

export default function UsersPage() {
    const [query, setQuery] = React.useState("");

    const { users, loading, error, fetchUsers, createUser, updateUser, deleteUser, getUserLoginHistory } = useUsers();
    const { companies, fetchCompanies } = useCompanies();

    React.useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("name");

    const [openCreate, setOpenCreate] = React.useState(false);
    const [openEdit, setOpenEdit] = React.useState(false);
    const [editingId, setEditingId] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    const [openDelete, setOpenDelete] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");

    const [historyOpen, setHistoryOpen] = React.useState(false);
    const [historyTarget, setHistoryTarget] = React.useState(null);
    const [historyRows, setHistoryRows] = React.useState([]);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [historyError, setHistoryError] = React.useState("");

    const blankForm = {
        username: "",
        email: "",
        password: "",
        passwordConfirm: "",
        firstname: "",
        lastname: "",
        role: "user",
        company_id: null,
        is_active: true,
    };

    const [form, setForm] = React.useState(blankForm);

    const onChange = (key) => (e) => {
        const value = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    /* ---------- Filter ---------- */
    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;

        return users.filter((u) =>
            (`${u.firstname || ""} ${u.lastname || ""}`.trim()).toLowerCase().includes(q) ||
            (u.username || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q) ||
            (u.company_name || "").toLowerCase().includes(q)
        );
    }, [users, query]);

    const rows = React.useMemo(
        () => stableSort(filtered, getComparator(order, orderBy)),
        [filtered, order, orderBy]
    );

    const columns = [
        { id: "name", label: "Name" },
        { id: "username", label: "Benutzername" },
        { id: "email", label: "E-Mail" },
        { id: "role", label: "Rolle" },
        { id: "company", label: "Firma" },
        { id: "last_login", label: "Last Login" },
    ];

    const handleRequestSort = (property) => () => {
        setOrder((prev) => (orderBy === property && prev === "asc" ? "desc" : "asc"));
        setOrderBy(property);
    };

    /* ---------- CRUD ---------- */
    const openCreateDialog = () => {
        setForm(blankForm);
        setSubmitError("");
        setOpenCreate(true);
    };

    const openEditDialog = (u) => {
        setEditingId(u.id);
        setForm({
            username: u.username || "",
            email: u.email || "",
            password: "",
            passwordConfirm: "",
            firstname: u.firstname || "",
            lastname: u.lastname || "",
            role: u.role || "user",
            company_id: u.company_id ?? null,
            is_active: !!u.is_active,
        });
        setSubmitError("");
        setOpenEdit(true);
    };

    const handleCreate = async () => {
        setSubmitError("");
        if (!form.username || !form.email || !form.password) {
            setSubmitError("Bitte Benutzername, E-Mail und Passwort ausfüllen.");
            return;
        }
        if (form.password !== form.passwordConfirm) {
            setSubmitError("Passwörter stimmen nicht überein.");
            return;
        }

        setSubmitting(true);
        try {
            await createUser({
                username: form.username.trim(),
                email: form.email.trim(),
                password: form.password,
                firstname: form.firstname || null,
                lastname: form.lastname || null,
                role: form.role,
                company_id: form.company_id ?? null,
                is_active: form.is_active,
            });
            setOpenCreate(false);
        } catch (e) {
            setSubmitError(e.message || "Erstellen fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async () => {
        setSubmitError("");
        if (!editingId) return;

        if (!form.username || !form.email) {
            setSubmitError("Bitte Benutzername und E-Mail ausfüllen.");
            return;
        }

        if ((form.password || form.passwordConfirm) && form.password !== form.passwordConfirm) {
            setSubmitError("Passwörter stimmen nicht überein.");
            return;
        }

        const payload = {
            username: form.username.trim(),
            email: form.email.trim(),
            firstname: form.firstname || null,
            lastname: form.lastname || null,
            role: form.role,
            company_id: form.company_id ?? null,
            is_active: form.is_active,
        };
        if (form.password) payload.password = form.password;

        setSubmitting(true);
        try {
            await updateUser(editingId, payload);
            setOpenEdit(false);
        } catch (e) {
            setSubmitError(e.message || "Aktualisierung fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const askDelete = (u) => {
        setDeleteTarget(u);
        setDeleteError("");
        setOpenDelete(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteUser(deleteTarget.id);
            setOpenDelete(false);
            setDeleteTarget(null);
        } catch (e) {
            setDeleteError(e.message || "Löschen fehlgeschlagen.");
        } finally {
            setDeleting(false);
        }
    };

    const openLoginHistory = async (u) => {
        setHistoryTarget(u);
        setHistoryRows([]);
        setHistoryError("");
        setHistoryOpen(true);
        setHistoryLoading(true);
        try {
            const data = await getUserLoginHistory(u.id);
            setHistoryRows(Array.isArray(data?.history) ? data.history : []);
        } catch (e) {
            setHistoryError(e?.response?.data?.error || e?.message || "Login-Historie laden fehlgeschlagen.");
        } finally {
            setHistoryLoading(false);
        }
    };

    return (
        <PageContainer
            title="Nutzerverwaltung"
            breadcrumbs={["Home", "Nutzer"]}
            actions={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {/* 🔍 Suchfeld – IDENTISCH zur CompaniesPage */}
                    <TextField
                        size="small"
                        placeholder="Suche… (Name, Benutzername, E-Mail, Rolle, Firma)"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{ minWidth: 320 }}
                    />

                    <Button variant="outlined" onClick={fetchUsers} disabled={loading}>
                        Aktualisieren
                    </Button>

                    <Button variant="contained" onClick={openCreateDialog}>
                        Neuen Nutzer anlegen
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((c) => (
                                    <TableCell key={c.id}>
                                        <TableSortLabel
                                            active={orderBy === c.id}
                                            direction={orderBy === c.id ? order : "asc"}
                                            onClick={handleRequestSort(c.id)}
                                        >
                                            {c.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((u) => (
                                <TableRow key={u.id} hover>
                                    <TableCell>{`${u.firstname || ""} ${u.lastname || ""}`.trim() || "-"}</TableCell>
                                    <TableCell>{u.username || "-"}</TableCell>
                                    <TableCell>{u.email || "-"}</TableCell>
                                    <TableCell>{u.role || "-"}</TableCell>
                                    <TableCell>{u.company_name || "-"}</TableCell>
                                    <TableCell>{formatDateTime(u.last_login_at)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Login-Historie">
                                            <IconButton size="small" onClick={() => openLoginHistory(u)}>
                                                <HistoryIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                        <IconButton size="small" onClick={() => openEditDialog(u)}>
                                            <EditIcon fontSize="inherit" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => askDelete(u)}
                                        >
                                            <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!rows.length && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                                        Keine Nutzer gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Dialoge (Create / Edit / Delete) sind unverändert */}
            <Dialog open={historyOpen} onClose={() => !historyLoading && setHistoryOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    Login-Historie {historyTarget?.username ? `- ${historyTarget.username}` : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {historyLoading ? (
                        <Box sx={{ py: 5, textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : historyError ? (
                        <Alert severity="error">{historyError}</Alert>
                    ) : historyRows.length ? (
                        <Stack spacing={1.5}>
                            {historyRows.map((row) => (
                                <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        {formatDateTime(row.logged_in_at)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        IP: {row.ip_address || "-"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                        User-Agent: {row.user_agent || "-"}
                                    </Typography>
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <Alert severity="info">Noch keine Logins protokolliert.</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)} disabled={historyLoading}>
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
